/*
 * Cart enhancements — free-shipping meter + cart recommendations
 * ------------------------------------------------------------------
 * 1. Free-shipping meter: `snippets/cart-summary.liquid` ships a freshly
 *    server-rendered copy of the meter inside a <template>. The theme re-renders
 *    that summary on every cart update (CartDrawer.build() swaps
 *    [data-cart-price-holder] with the api-cart-items response), so this just
 *    swaps the visible meter for the template's copy. No money formatting in JS.
 *
 * 2. Recommendations: fetches sections/cart-recommendations through Shopify's
 *    Product Recommendations API, anchored on the cart's biggest line, and drops
 *    the result into [data-cart-recommendations]. Tries `complementary` first
 *    (Search & Discovery relationships) and falls back to `related`.
 *
 * Re-runs on `theme:cart:added` (any cart change) and `theme:cart:loaded` (the
 * drawer's first render, which clones its markup out of a <template>).
 */
(function () {
  'use strict';

  var selectors = {
    summary: '[data-cart-summary]',
    ship: '[data-cart-ship]',
    shipFragment: 'template[data-cart-ship-fragment]',
    recsHolder: '[data-cart-recommendations]',
    recsContent: '[data-cart-recommendations-content]',
    recCard: '[data-cart-rec-card]',
  };

  var REC_SECTION = 'cart-recommendations';
  var REC_LIMIT = 6;

  // Key of the cart state the currently shown recommendations were built for,
  // so an unrelated re-render (a note edit, a drawer re-open) doesn't refetch.
  var renderedKey = null;
  var inFlight = null;

  // Products the shopper added straight from a suggestion. They stay out of the
  // list for the rest of the session, even if a later fetch offers them again.
  var dismissed = {};

  function summaryEl() {
    return document.querySelector(selectors.summary);
  }

  function cardCount(container) {
    return container.querySelectorAll(selectors.recCard).length;
  }

  // Identifies the cart state the recommendations are derived from: the anchor
  // product plus everything in the cart.
  function cartKey() {
    var summary = summaryEl();
    if (!summary) return null;

    return (
      (summary.getAttribute('data-cart-rec-source') || '') +
      '|' +
      (summary.getAttribute('data-cart-product-ids') || '')
    );
  }

  function recommendationsUrl(productId, intent) {
    var routes = (window.theme && window.theme.routes) || {};
    var base = routes.product_recommendations_url || '/recommendations/products';

    return (
      base +
      (base.indexOf('?') > -1 ? '&' : '?') +
      'section_id=' +
      encodeURIComponent(REC_SECTION) +
      '&limit=' +
      REC_LIMIT +
      '&intent=' +
      encodeURIComponent(intent) +
      '&product_id=' +
      encodeURIComponent(productId)
    );
  }

  /* ---------------------------------------------------------------- meter -- */

  function syncShippingMeter() {
    var summary = summaryEl();
    if (!summary) return;

    var template = summary.querySelector(selectors.shipFragment);
    if (!template || !template.content) return;

    var fresh = template.content.querySelector(selectors.ship);
    var current = document.querySelectorAll(selectors.ship);

    Array.prototype.forEach.call(current, function (meter) {
      if (fresh) {
        meter.parentNode.replaceChild(fresh.cloneNode(true), meter);
      } else {
        meter.parentNode.removeChild(meter);
      }
    });
  }

  /* ------------------------------------------------------- recommendations -- */

  // Removes cards for products that are already in the cart or were added from
  // a suggestion earlier. A cached recommendations response can outlive the cart
  // change that invalidated it, so this filter is applied to fetched markup as
  // well as to what is already on the page. `remember` marks what it removes as
  // dismissed, and is only set for cards the shopper actually saw and added.
  // Returns how many cards it removed.
  function dropCartProducts(container, cartProductIds, remember) {
    var inCart = ',' + (cartProductIds || '');
    var cards = container.querySelectorAll(selectors.recCard);
    var removed = 0;

    Array.prototype.forEach.call(cards, function (card) {
      var id = card.getAttribute('data-product-id');
      if (!id) return;
      if (inCart.indexOf(',' + id + ',') === -1 && !dismissed[id]) return;

      if (remember) dismissed[id] = true;
      card.parentNode.removeChild(card);
      removed += 1;
    });

    return removed;
  }

  function parseResponse(html, cartProductIds) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var content = doc.querySelector(selectors.recsContent);
    if (!content) return null;

    dropCartProducts(content, cartProductIds, false);

    return cardCount(content) ? content : null;
  }

  function paint(content) {
    var holders = document.querySelectorAll(selectors.recsHolder);

    Array.prototype.forEach.call(holders, function (holder) {
      holder.innerHTML = content ? content.innerHTML : '';
      holder.classList.toggle('hidden', !content);
    });
  }

  // Drop any card whose product is now in the cart, synchronously — without this
  // the product you just added sits there looking un-added. Returns how many
  // cards went, which is what tells run() the add came from a suggestion.
  function pruneAddedProducts() {
    var summary = summaryEl();
    if (!summary) return 0;

    var cartProductIds = summary.getAttribute('data-cart-product-ids') || '';
    var holders = document.querySelectorAll(selectors.recsHolder);
    var removed = 0;

    Array.prototype.forEach.call(holders, function (holder) {
      removed += dropCartProducts(holder, cartProductIds, true);

      if (!cardCount(holder)) {
        holder.innerHTML = '';
        holder.classList.add('hidden');
      }
    });

    return removed;
  }

  // Fade the card as soon as it is clicked, so there is feedback during the add
  // rather than a stationary button. `theme:cart:added` removes it outright; the
  // failsafe restores it if the add errors (out of stock, cart limits), since
  // the theme fires no event on that path.
  function markAdding(event) {
    var button = event.target.closest && event.target.closest('[data-add-to-cart]');
    if (!button) return;

    var card = button.closest(selectors.recCard);
    if (!card) return;

    card.classList.add('is-adding');
    setTimeout(function () {
      card.classList.remove('is-adding');
    }, 6000);
  }

  function loadRecommendations() {
    if (!document.querySelector(selectors.recsHolder)) return;

    var summary = summaryEl();
    var sourceId = summary && summary.getAttribute('data-cart-rec-source');
    var cartProductIds = (summary && summary.getAttribute('data-cart-product-ids')) || '';
    var count = summary ? parseInt(summary.getAttribute('data-cart-count'), 10) || 0 : 0;

    if (!sourceId || !count) {
      renderedKey = null;
      paint(null);
      return;
    }

    var key = cartKey();
    if (key === renderedKey) return;

    if (inFlight) inFlight.abort();
    inFlight = typeof AbortController === 'function' ? new AbortController() : null;
    var signal = inFlight ? inFlight.signal : undefined;

    var request = function (intent) {
      return fetch(recommendationsUrl(sourceId, intent), { signal: signal }).then(function (response) {
        if (!response.ok) throw new Error('Recommendations request failed: ' + response.status);
        return response.text();
      });
    };

    request('complementary')
      .then(function (html) {
        var content = parseResponse(html, cartProductIds);
        // Complementary relationships are merchant-curated and often unset;
        // `related` is always available.
        return content || request('related').then(function (fallback) {
          return parseResponse(fallback, cartProductIds);
        });
      })
      .then(function (content) {
        renderedKey = key;
        paint(content);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        renderedKey = null;
        paint(null);
      });
  }

  /* ------------------------------------------------------------------ run -- */

  function run() {
    syncShippingMeter();

    // Adding a suggestion leaves a gap, deliberately. Refetching here would
    // slide a replacement product into the empty slot, which reads as the add
    // having done nothing. Pinning the rendered key to the cart's new state
    // skips this one fetch; any later cart change still refreshes normally.
    if (pruneAddedProducts()) {
      if (inFlight) inFlight.abort();
      renderedKey = cartKey();
      return;
    }

    loadRecommendations();
  }

  document.addEventListener('theme:cart:added', run);
  document.addEventListener('theme:cart:loaded', run);
  document.addEventListener('click', markAdding, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
